import { Button, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";

import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";

/**
 * GenerateGedExternosColumnDefinitions
 */

type Props = {
  onActionEdit: (idArquivoModulo: number | null, openState: boolean) => void;
  onActionDelete: (idArquivoModulo: number | null, openState: boolean) => void;
  onActionCopy: (idArquivoModulo: number | null, openState: boolean) => void;
  onActionVisualize: (idArquivoModulo: number) => void;
  onActionDownload: (idArquivoModulo: number) => void;
};

export const GenerateGedExternosColumnDefinitions = ({
  onActionEdit,
  onActionDelete,
  onActionCopy,
  onActionVisualize,
  onActionDownload,
}: Props): GridColDef[] => {

  const computeItemSituacaoTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Em dia";
      case 2:
        return "Vencido";
      default:
        return "?";
    }
  };

  const computeItemSituacaoColor = (status: number) => {
    switch (status) {
      case 1:
        return "#45b6af";
      case 2:
        return "#f3565d";
      default:
        return "blue";
    }
  };

  const computeItemTipoTitle = (status: number) => {
    switch (status) {
      case 1:
        return "Atual";
      case 2:
        return "Obsoleto";
      default:
        return "?";
    }
  };

  const computeItemTipoColor = (status: number) => {
    switch (status) {
      case 1:
        return "#45b6af";
      case 2:
        return "#f3565d";
      default:
        return "blue";
    }
  };

  return [
    {
      field: "statusVencimento",
      filterable: false,
      headerName: "Em dia ou Vencido?",
      width: 80,
      minWidth: 80,
      sortable: false,
      valueGetter: (_, row) => row['dataVencimento'],
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <div>
            <span
              className='inline px-2 py-1 text-sm rounded'
              style={{ backgroundColor: computeItemSituacaoColor(value !== null && dayjs.utc(value).local().isBefore(Date.now()) ? 2 : 1) }}>
              {computeItemSituacaoTitle(value !== null && dayjs.utc(value).local().isBefore(Date.now()) ? 2 : 1)}
            </span>
          </div>
        </div>
      )
    },
    {
      field: "obsoleto",
      filterable: false,
      headerName: "Atual ou Obsoleto?",
      width: 80,
      minWidth: 80,
      sortable: false,
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-center items-center">
          <div>
            <span
              className='inline px-2 py-1 text-sm rounded'
              style={{ backgroundColor: computeItemTipoColor(value === true ? 2 : 1) }}>
              {computeItemTipoTitle(value === true ? 2 : 1)}
            </span>
          </div>
        </div>
      )
    },
    {
      field: "descricao",
      filterable: false,
      headerName: "Nome",
      flex: 1,
      minWidth: 200,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => (
        <div className="w-full h-full flex justify-start items-center break-normal">
          <span className="text-sm">{value}</span>
        </div>
      )
    },
    {
      field: "visualizar",
      filterable: false,
      headerName: "Visualizar",
      width: 175,
      sortable: false,
      resizable: false,
      valueGetter: (_, rows) => ({
        arquivoNome: rows['nomePdf'],
        idArquivoModulo: rows['idArquivoModulo'],
      }),
      renderCell: (params) => {
        const maxSize = 20;
        const arquivoNome = params.value['arquivoNome'] as string;
        const idArquivoModulo = params.value['idArquivoModulo'] as number;

        return (
          <Button
            onMouseDown={() => onActionVisualize(idArquivoModulo)}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.3rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
              width: '9.5rem',
            }}>
            {arquivoNome && arquivoNome.length > maxSize ? `${arquivoNome.substring(0, maxSize)}...` : arquivoNome}
          </Button>
        );
      },
    },
    {
      field: "download",
      filterable: false,
      headerName: "Download",
      width: 175,
      sortable: false,
      resizable: false,
      valueGetter: (_, rows) => ({
        arquivoNome: rows['nome'],
        idArquivoModulo: rows['idArquivoModulo'],
      }),
      renderCell: (params) => {
        const maxSize = 20;
        const arquivoNome = params.value['arquivoNome'] as string;
        const idArquivoModulo = params.value['idArquivoModulo'] as number;

        return (
          <Button
            onMouseDown={() => onActionDownload(idArquivoModulo)}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.3rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
              width: '9.5rem',
            }}>
            {arquivoNome && arquivoNome.length > maxSize ? `${arquivoNome.substring(0, maxSize)}...` : arquivoNome}
          </Button>
        );
      },
    },
    {
      field: "conferirAtualizacao",
      filterable: false,
      headerName: "Data de Conferência",
      width: 110,
      minWidth: 110,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => <span>{value !== null ? dayjs.utc(value).local().format('DD/MM/YYYY') : ''}</span>
    },
    {
      field: "versao",
      filterable: false,
      headerName: "Versão",
      width: 80,
      minWidth: 80,
      sortable: false,
      align: 'left',
    },
    {
      field: "dataVencimento",
      filterable: false,
      headerName: "Data Vencimento",
      width: 110,
      minWidth: 110,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => <span>{value !== null ? dayjs.utc(value).local().format('DD/MM/YYYY') : ''}</span>
    },
    {
      field: 'idArquivoModulo',
      sortable: false,
      filterable: false,
      headerName: 'Ações',
      minWidth: 100,
      renderCell: (params) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onActionEdit(params.value, true)}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => onActionDelete(params.value, true)}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
    {
      field: 'idArquivoModuloCopia',
      sortable: false,
      filterable: false,
      headerName: 'Copiar',
      width: 65,
      valueGetter: (_, row) => row['idArquivoModulo'] as number,
      renderCell: (params) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onActionCopy(params.value, true)}>
              <DynamicIcon nameIcon="ContentCopy" size={25} className="text-purple-500" />
            </IconButton>
          </div>
        );
      }
    }
  ];
}